/* Graph of Life brand v1.0.0 · tokens.json */
import React from 'react';
import { motion } from 'framer-motion';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import SectionHeading from '@/components/gol/SectionHeading';
import CaveatCallout from '@/components/gol/CaveatCallout';
import { ReviewCta, GhostLink } from '@/components/gol/ReviewCta';
import VennDiagram from '@/components/visualizations/VennDiagram';
import CapabilityGraph from '@/components/visualizations/CapabilityGraph';
import LinkedinDispatches from '@/components/sections/LinkedinDispatches';
import { ReadingHeader } from '@/components/gol/ReadingHeader';
import { ReadingNext } from '@/components/gol/ReadingNext';

const credentials = [
  {
    section: 'Scientific core',
    title: 'Postdoctoral research at VIB and VLIZ in computational systems biology, marine bioinformatics and systems ecology',
  },
  {
    section: 'Venture and applied science',
    title: 'Ecological data scientist and semantic ontology engineer',
    subtitle: 'Blue finance · regenerative agriculture · former Chief Scientist, NewAtlantis Labs',
  },
  {
    section: 'Research frontier',
    title: 'Agricultural systems and genomic language modelling',
    subtitle: 'Bio-surveillance · protein and sequence architectures',
  },
  {
    section: 'Earth observation',
    title: 'Geospatial intelligence and blue-carbon measurement',
    subtitle: 'Blue-carbon evidence assembly · ecosystem monitoring',
  },
];

const partners = [
  {
    name: 'Octavio Aburto-Oropeza, PhD',
    title: 'Scientific Director · Scripps Institution of Oceanography',
    desc: 'Marine ecologist with more than twenty years in the Gulf of California. The monitoring archive spans 600+ reefs, 800 species and millions of fish observations.',
  },
  {
    name: 'Fabio Favoretto, PhD',
    title: 'Valuation Lead · Scripps Institution of Oceanography',
    desc: 'Lead author, Science Advances (2023), showing that Mexico\u2019s largest marine protected area conserved biodiversity without reducing fishing catches. Works on satellite tracking and AI-based compliance monitoring.',
  },
  {
    name: 'Adri\u00e1n Munguia-Vega, PhD',
    title: 'Connectivity Lead · University of Arizona',
    desc: 'Conservation geneticist and co-lead author of the One Earth climate-smart marine protected area framework. Larval dispersal and eDNA protocols central to understanding site resilience. Connectivity lead on BlueGraph.',
  },
];

const domains = [
  {
    id: '01',
    title: 'Ecosystem network modelling',
    question: 'How do cascading effects propagate through ecosystems?',
    desc: 'I model species interactions and energy flows to investigate where resilience concentrates, where it thins and which relationships deserve closer attention.',
  },
  {
    id: '02',
    title: 'Natural-capital evidence',
    question: 'What evidence would a monitoring or claim requirement actually need?',
    desc: 'I build measurement and provenance architecture for blue carbon, soil health and biodiversity, making missing observations part of the record.',
  },
  {
    id: '03',
    title: 'Ecological knowledge systems',
    question: 'How do we organize ecological complexity so a decision can be inspected?',
    desc: 'I design governed knowledge graphs that carry ecological relationships, their sources and uncertainty into automated reasoning and accountable human review.',
  },
  {
    id: '04',
    title: 'Biological sequence intelligence',
    question: 'What if we could read genomes the way we read documents?',
    desc: 'I apply language-model architectures to DNA, RNA and protein sequences, treating molecular data as evidence of ecological function that can be inspected.',
  },
];

const evidenceRecord = [
  {
    title: 'Spatial decision graph (BlueGraph)',
    context:
      'Six independent evidence streams across the Gulf of California had no shared spatial or evidentiary grammar connecting them to a bounded decision.',
    outcome:
      'A governed reasoning graph spanning 10,913 planning units, 97,573 nodes and 514,728 relationships, publishing committed decision traces with explicit evidence tiers.',
    limit: 'Screening-grade evidence governance, bounded away from financeable credit and final investment conclusions.',
  },
  {
    title: 'Marine trophic intelligence',
    context:
      'Marine food-web knowledge was scattered across sources, leaving species interactions disconnected from the pathways through which stress propagates.',
    outcome:
      'Knowledge graph encoding more than 11,900 species and 20M trophic relationships, supporting keystone identification and cascade exploration.',
    limit: 'Inferred ecological relationships for investigation. Calibrated risk, pricing and covenant modelling sit outside the current scope.',
  },
  {
    title: 'Marine biodiversity knowledge graph',
    context:
      'Marine genomic data was spread across databases, leaving ecological inference and bioprospecting without a unified way to query it.',
    outcome:
      'Graph system with 30.5M nodes and 170M relationships, integrating molecular metadata into a queryable ontological framework.',
    limit: 'Research infrastructure. Institutional use requires domain validation and accountable review.',
  },
];

const About = () => (
  <div data-testid="about-page" className="bg-abyss-950 text-sand-50 relative overflow-x-hidden">
    <InteractiveBackground viewMode="ECOLOGICAL" density={0.85} />

    <section className="relative px-5 md:px-8 pt-32 md:pt-40 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto space-y-8"
      >
        <ReadingHeader id="about" category="Scientific foundation" title="Jay Gutierrez. Research made useful across systems." intro="I am a network scientist, systems biologist and cross-system architect. I work where scientific research, knowledge graphs, AI, software and institutional strategy meet, so the dependencies behind consequential decisions can be examined rather than assumed." />
      </motion.div>
    </section>

    <section
      data-testid="about-credentials"
      className="relative px-5 md:px-8 py-20 border-t border-abyss-800 bg-abyss-900"
    >
      <div className="max-w-6xl mx-auto grid gap-12 lg:grid-cols-[minmax(0,1fr)_1.3fr] items-start">
        <img
          src="/images/jay.jpg"
          alt="Jayson Gutierrez"
          className="w-full max-w-xs aspect-square object-cover rounded-sm border border-abyss-700"
        />
        <div className="space-y-8">
          <SectionHeading eyebrow="Roles and training" heading="Where the work comes from" />
          <dl className="space-y-6">
            {credentials.map((c) => (
              <div key={c.section} className="border-t border-abyss-800 pt-4 space-y-1.5">
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">{c.section}</dt>
                <dd className="text-base text-sand-50">{c.title}</dd>
                {c.subtitle && <dd className="font-mono text-[11px] text-teal-400">{c.subtitle}</dd>}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>

    <section data-testid="about-positioning" className="relative px-5 md:px-8 py-24 border-t border-abyss-800">
      <div className="max-w-6xl mx-auto space-y-12">
          <SectionHeading eyebrow="Computational background" heading="Seven disciplines held by one investigative practice">
          <p>
            I work across fields that observe the world at different scales and speak in different forms of evidence.
            The connective capability is to map their dependencies, trace how failures propagate and turn the
            resulting picture into intelligence that people can inspect, challenge and use.
          </p>
        </SectionHeading>

        <CapabilityGraph />

        <div className="pt-8 space-y-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">The intersection, stated plainly</p>
          <div className="max-w-2xl">
            <VennDiagram />
          </div>
        </div>
      </div>
    </section>

    <section
      data-testid="about-domains"
      className="relative px-5 md:px-8 py-24 border-t border-abyss-800 bg-abyss-900"
    >
      <div className="max-w-6xl mx-auto space-y-12">
        <SectionHeading eyebrow="Scientific domains" heading="Four bodies of work behind the practice" />
        <div className="grid gap-8 md:grid-cols-2">
          {domains.map((d) => (
            <div key={d.id} data-testid={`about-domain-${d.id}`} className="space-y-3 border-t border-abyss-800 pt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">Domain {d.id}</p>
              <h3 className="font-serif text-xl md:text-2xl font-light text-sand-50">{d.title}</h3>
              <p className="font-mono text-[11px] text-teal-400 border-l border-teal-900 pl-3">
                {d.question}
              </p>
              <p className="text-sm leading-relaxed text-sand-50/70">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-evidence" className="relative px-5 md:px-8 py-24 border-t border-abyss-800">
      <div className="max-w-6xl mx-auto space-y-12">
        <SectionHeading eyebrow="Evidence record" heading="What I have built, and where the evidence still ends">
          <p>
            My former role as Chief Scientist at NewAtlantis Labs brought the research into practical settings,
            while postdoctoral work at VIB and VLIZ kept the methods close to their scientific foundations. When a
            committee asks how a conclusion was reached, provenance matters. The path from observation to metric
            must remain replayable and open to challenge.
          </p>
        </SectionHeading>
        <div className="grid gap-6 lg:grid-cols-3">
          {evidenceRecord.map((e) => (
            <article
              key={e.title}
              data-testid={`about-evidence-${e.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="gol-panel gol-panel-live flex flex-col gap-5 p-7"
            >
              <h3 className="font-serif text-lg md:text-xl font-light text-sand-50">{e.title}</h3>
              <div className="space-y-1.5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">Context</p>
                <p className="text-sm leading-relaxed text-sand-50/70">{e.context}</p>
              </div>
              <div className="space-y-1.5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-50/35">Outcome</p>
                <p className="text-sm leading-relaxed text-sand-50/70">{e.outcome}</p>
              </div>
              <CaveatCallout testId={`about-evidence-limit-${e.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {e.limit}
              </CaveatCallout>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section
      data-testid="about-partners"
      className="relative px-5 md:px-8 py-24 border-t border-abyss-800 bg-abyss-900"
    >
      <div className="max-w-6xl mx-auto space-y-12">
        <SectionHeading eyebrow="Scientific partners" heading="A practice strengthened through collaboration">
          <p>
            I build with researchers whose observations hold the work to the world. Their expertise across marine
            ecology, conservation genetics and spatial assessment keeps each graph accountable to the systems it
            represents.
          </p>
        </SectionHeading>
        <div className="grid gap-8 md:grid-cols-3">
          {partners.map((p) => (
            <div key={p.name} className="space-y-2 border-t border-abyss-800 pt-5">
              <p className="font-serif text-lg md:text-xl font-light text-sand-50">{p.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-teal-400">{p.title}</p>
              <p className="text-sm leading-relaxed text-sand-50/70">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <LinkedinDispatches />

    <section className="relative px-5 md:px-8 py-24 border-t border-abyss-800">
      <div className="max-w-6xl mx-auto space-y-8">
        <SectionHeading eyebrow="Next step" heading="Bring one decision that depends on nature." />
        <div className="flex flex-wrap items-center gap-6">
          <GhostLink to="/systems" testId="about-proof-link">
            Inspect the proof library
          </GhostLink>
        </div>
        <ReadingNext id="about" to="/knowledge" title="Explore the knowledge architecture." copy="The computational approach connecting scientific disciplines to inspectable evidence." />
      </div>
    </section>
  </div>
);

export default About;
